// Compute employer taxes from normalized employee data and compare against the
// "actual paid" numbers extracted by the ADP transformer (or any transformer
// that populates `validation.actual*`).
//
// The math here intentionally mirrors how the existing engine would compute
// ER taxes WITHOUT a Section 125 plan in place — i.e., the baseline. That is
// what's reported on the TriMed Original Data tab and is the reference point
// for the validation panel.

const DEFAULT_FED_CONSTANTS = {
  ssRate: 0.062,
  ssWageBase: 168600, // 2024 IRS limit, conservative default
  medicareRate: 0.0145,
  futaRate: 0.006,
  futaWageBase: 7000,
};

function getConstants(config) {
  const pc = config?.payrollConstants || {};
  const es = config?.engineSettings || {};
  return {
    ssRate: pc.ssRate ?? DEFAULT_FED_CONSTANTS.ssRate,
    ssWageBase: pc.ssWageBase ?? DEFAULT_FED_CONSTANTS.ssWageBase,
    medicareRate: pc.medicareRate ?? DEFAULT_FED_CONSTANTS.medicareRate,
    futaRate: es.futaRate ?? DEFAULT_FED_CONSTANTS.futaRate,
    futaWageBase: es.futaWageBase ?? DEFAULT_FED_CONSTANTS.futaWageBase,
  };
}

export function computeEmployerTaxesForEmployee(salary, config) {
  const c = getConstants(config);
  const s = Number(salary) || 0;
  const ssTax = Math.min(s, c.ssWageBase) * c.ssRate;
  const medicareTax = s * c.medicareRate;
  const futaTax = Math.min(s, c.futaWageBase) * c.futaRate;
  return { ssTax, medicareTax, futaTax };
}

// Compute reconciliation between actual and our calculations.
// Returns a structured result the UI can render directly.
export function computeTaxValidation(employees, config) {
  if (!Array.isArray(employees) || employees.length === 0) {
    return null;
  }
  let totalActualSs = 0;
  let totalActualMedicare = 0;
  let totalActualFuta = 0;
  let totalActualState = 0;

  let totalCalcSs = 0;
  let totalCalcMedicare = 0;
  let totalCalcFuta = 0;

  let employeesWithActuals = 0;
  let totalSalary = 0;

  for (const emp of employees) {
    const v = emp.validation || {};
    if (
      v.actualFutaEr !== undefined ||
      v.actualMedicareEr !== undefined ||
      v.actualSsEr !== undefined
    ) {
      employeesWithActuals += 1;
    }
    totalActualSs += Number(v.actualSsEr) || 0;
    totalActualMedicare += Number(v.actualMedicareEr) || 0;
    totalActualFuta += Number(v.actualFutaEr) || 0;
    totalActualState += Number(v.actualStateEr) || 0;

    const taxes = computeEmployerTaxesForEmployee(emp.salary || emp.annualSalary || 0, config);
    totalCalcSs += taxes.ssTax;
    totalCalcMedicare += taxes.medicareTax;
    totalCalcFuta += taxes.futaTax;
    totalSalary += Number(emp.salary || emp.annualSalary || 0);
  }

  if (employeesWithActuals === 0) return null;

  const lines = [
    {
      key: 'ss',
      label: 'Social Security ER',
      actual: totalActualSs,
      calc: totalCalcSs,
    },
    {
      key: 'medicare',
      label: 'Medicare ER',
      actual: totalActualMedicare,
      calc: totalCalcMedicare,
    },
    {
      key: 'futa',
      label: 'FUTA',
      actual: totalActualFuta,
      calc: totalCalcFuta,
    },
  ].map((line) => {
    const variance = line.actual === 0 ? 0 : ((line.calc - line.actual) / line.actual);
    const absVar = Math.abs(variance);
    let status = 'good';
    if (absVar > 0.02) status = 'high';
    else if (absVar > 0.005) status = 'warn';
    return { ...line, variance, absVar, status };
  });

  const maxAbsVar = Math.max(...lines.map((l) => l.absVar));
  let overall = 'good';
  if (maxAbsVar > 0.02) overall = 'high';
  else if (maxAbsVar > 0.005) overall = 'warn';

  return {
    lines,
    totals: {
      actualSs: totalActualSs,
      actualMedicare: totalActualMedicare,
      actualFuta: totalActualFuta,
      actualState: totalActualState,
      calcSs: totalCalcSs,
      calcMedicare: totalCalcMedicare,
      calcFuta: totalCalcFuta,
      totalSalary,
    },
    maxAbsVariance: maxAbsVar,
    overall,
    employeesWithActuals,
    employeesTotal: employees.length,
  };
}
